import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { faqGroups, siteInfo } from "@/mocks/site";

export default function Faq() {
  const allItems = faqGroups.flatMap((group) => group.items);

  useSeo({
    title: "常见问题 - 阅读方式、内容原则与线索提交说明 | 爆料网上",
    description:
      "解答关于爆料网上的常见疑问：内容如何整理、时间线节点怎么排序、线索如何提交、内容更正流程，以及联系方式与回复时间。",
    keywords: "爆料网上常见问题,事件时间线说明,线索提交,内容更正",
    path: "/faq",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: allItems.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://huahuoyouxi.cn/" },
          { "@type": "ListItem", position: 2, name: "常见问题", item: "https://huahuoyouxi.cn/faq" },
        ],
      },
    ],
  });

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-5 md:px-10 pt-24 md:pt-32 pb-10 md:pb-14">
          <Breadcrumb items={[{ label: "常见问题" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.22em] text-primary-600 font-medium">
              <span className="w-5 h-px bg-primary-400" />
              常见问题
            </span>
            <h1 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              关于阅读、内容与线索的常见疑问
            </h1>
            <p className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
              这里汇总了读者问得最多的问题。如果下面没有你想问的内容，
              可以直接把问题发到站点邮箱，或在首页的线索通道留言。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-12 md:py-16 bg-background-50">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          <aside className="lg:w-[26%]">
            <div className="lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">问题分类</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {faqGroups.map((group) => (
                  <li key={group.group}>
                    <a
                      href={`#${group.group}`}
                      className="flex items-center justify-between gap-2 py-1.5 text-foreground-600 hover:text-primary-700 cursor-pointer"
                    >
                      <span className="inline-flex items-center gap-2">
                        <i className="ri-subtract-line text-foreground-300" />
                        {group.group}
                      </span>
                      <span className="text-[11px] text-foreground-400">{group.items.length}</span>
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-background-200">
                <p className="text-xs text-foreground-500 mb-2">仍然没有答案？</p>
                <Link
                  to="/#tips"
                  className="inline-flex w-full items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-chat-3-line" />
                  留下你的问题
                </Link>
              </div>
            </div>
          </aside>

          <div className="lg:w-[74%] space-y-10">
            {faqGroups.map((group) => (
              <section key={group.group} id={group.group} className="scroll-mt-24">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 text-sm">
                    <i className="ri-question-line" />
                  </span>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                    {group.group}
                  </h2>
                </div>
                <div className="mt-4">
                  <FaqAccordion items={group.items} />
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-12 md:py-16 bg-background-100">
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-6 md:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              还有其他问题？
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              涉及内容更正、线索核实或合作沟通，欢迎直接发邮件给我们，
              我们会在工作日内回复。邮箱：{siteInfo.email}
            </p>
          </div>
          <Link
            to="/about"
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer lg:shrink-0"
          >
            了解我们的内容原则
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </>
  );
}